import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import {
  InteractionFactoryEntity,
  ReconcileContext,
} from "../../features/interaction_factory/InteractionFactory";
import Interaction, { InteractionType } from "./Interaction";
import AttachPreview from "../../features/previews/attach/AttachPreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import AttachNodePreview from "../../features/sequence/node_previews/AttachNodePreview";
import {
  compactSearchableStrings,
  getBooleanSearchLabels,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export interface IAttach extends Interaction {
  playerGuid: string;
  propGuid: string;
  prefabName: string;
  tag: string;
  playerToProp: boolean;
}

export const attachInteraction = (): InteractionFactoryEntity => {
  const component = <AttachPreview />;

  const create = (getKeys: (amount: number) => string[]): IAttach => {
    return {
      id: GetNewGuid(),
      name: "Attach",
      type: InteractionType.Attach,
      playerGuid: "",
      propGuid: "",
      prefabName: "",
      tag: "",
      playerToProp: false,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IAttach => {
    const converted = interaction as IAttach;

    if (converted.playerToProp === null) converted.playerToProp = false;

    return converted;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): IAttach => {
    const original = interaction as IAttach;
    return {
      ...original,
      id: GetNewGuid(),
    };
  };

  const reconcile = (
    interaction: Interaction,
    context: ReconcileContext
  ): IAttach => {
    const attach = interaction as IAttach;
    const fresh = context.prefabNameByInstanceId[attach.propGuid];
    if (!fresh || fresh === attach.prefabName) return attach;
    context.changes.push({
      scope: "interaction",
      id: attach.id,
      label: attach.name,
      field: "prefabName",
      oldValue: attach.prefabName,
      newValue: fresh,
    });
    return { ...attach, prefabName: fresh };
  };

  const validateErrors = (interaction: Interaction): string[] => {
    const attach = interaction as IAttach;
    const errors: string[] = [];

    if (!attach.playerGuid?.trim()) errors.push("Actor is required");
    if (!attach.propGuid?.trim()) errors.push("Prop is required");

    return errors;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const converted = interaction as IAttach;
    const searchableStrings = compactSearchableStrings(
      interaction.name,
      searchLookupContext?.getActorSearchTerms(converted.playerGuid) ??
        converted.playerGuid,
      searchLookupContext?.getPropSearchTerms(converted.propGuid) ??
        converted.propGuid,
      converted.prefabName,
      converted.tag,
      getBooleanSearchLabels(
        converted.playerToProp,
        "Player to Prop",
        "Prop to Player",
      ),
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "Attach";

  return {
    View: component,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <AttachNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Reconcile: reconcile,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
